import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import API from "../../lib/api";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

const KIND_COLOURS = { injury: "#dc2626", near_miss: "#f59e0b", other: "#64748b" };

/** Monthly incident / near-miss trend with the three cause categories that keep coming up. */
export default function IncidentTrendsCard() {
  const [months, setMonths] = useState(12);
  const [d, setD] = useState(null);
  useEffect(() => { API.get(`/hse/incidents/trends?months=${months}`).then(({ data }) => setD(data)).catch(() => {}); }, [months]);
  if (!d) return null;
  const Trend = d.trend.direction === "up" ? TrendUp : d.trend.direction === "down" ? TrendDown : Minus;
  const trendTone = d.trend.direction === "up" ? "text-red-600" : d.trend.direction === "down" ? "text-emerald-600" : "text-muted-foreground";

  return (
    <div className="grid lg:grid-cols-3 gap-3" data-testid="inc-trends">
      <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold">Incidents by month</p>
            <p className="text-xs text-muted-foreground" data-testid="inc-trends-totals">
              {d.totals.total} in {d.months} months · {d.totals.injury} injur{d.totals.injury === 1 ? "y" : "ies"}, {d.totals.near_miss} near miss{d.totals.near_miss === 1 ? "" : "es"}, {d.totals.other} other
              {d.totals.injury > 0 && <> · {d.ratio_near_miss_per_injury} near misses reported per injury</>}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 text-xs font-medium ${trendTone}`} title={`${d.trend.previous_half} in the first half vs ${d.trend.current_half} in the second`} data-testid="inc-trends-direction"><Trend size={14} weight="bold" /> {d.trend.direction === "flat" ? "steady" : `${d.trend.direction} vs earlier half`}</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="inc-trends-months">
            <option value={6}>6 months</option><option value={12}>12 months</option><option value={24}>24 months</option>
          </select>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={d.series} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={months > 12 ? 1 : 0} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, name) => [v, { injury: "Injuries", near_miss: "Near misses", other: "Other" }[name] || name]} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => ({ injury: "Injuries", near_miss: "Near misses", other: "Other (ill health, dangerous occurrence, damage)" }[v] || v)} />
            <Bar dataKey="injury" stackId="a" fill={KIND_COLOURS.injury} />
            <Bar dataKey="near_miss" stackId="a" fill={KIND_COLOURS.near_miss} />
            <Bar dataKey="other" stackId="a" fill={KIND_COLOURS.other} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-card border border-border rounded-lg p-4" data-testid="inc-top-causes">
        <p className="text-sm font-semibold">Top causes</p>
        <p className="text-xs text-muted-foreground mb-3" data-testid="inc-top-causes-source">From {d.investigated} investigated incident{d.investigated === 1 ? "" : "s"} in the period{d.keyword_only > 0 ? ` · ${d.tagged} tagged, ${d.keyword_only} by keyword` : d.tagged > 0 ? " · all tagged" : ""}</p>
        {d.top_causes.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="inc-top-causes-empty">No investigated incidents yet — run "AI investigate" on an incident and its causes will show here.</p>
        ) : (
          <ol className="space-y-3">
            {d.top_causes.map((c, i) => (
              <li key={c.key} className="text-xs" data-testid={`inc-top-cause-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary font-bold text-[11px] inline-flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="font-medium flex-1">{c.label}</span>
                  <span className="text-muted-foreground tabular-nums">{c.count} · {c.pct}%</span>
                </div>
                <div className="ml-7 mt-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary/70" style={{ width: `${c.pct}%` }} /></div>
                {c.examples[0] && <p className="ml-7 mt-1 text-muted-foreground italic line-clamp-2">“{c.examples[0]}”</p>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
